import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Minus, Plus, ShieldCheck, Trash2, X } from "lucide-react";

import { formatMoney } from "@/components/shopui";
import { Button, Card, IconCircle, ScreenHeader, Spinner } from "@/components/kit";
import { Page } from "@/components/layout";
import { Footer } from "@/pages/ScanResults";
import { useShop } from "@/shop";

export default function Cart() {
  const { cart, cartBusy, updateLine, removeLine, refreshCart, checkout } = useShop();
  const navigate = useNavigate();
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);
  const empty = !cart || cart.lines.length === 0;

  return (
    <div data-testid="cart-screen">
      <ScreenHeader testId="cart-back" label="Your cart" fallback="/shop" right={cartBusy ? <Spinner /> : null} />
      <Page className="flex flex-col gap-3 pb-56">
        <h1 data-testid="cart-title" className="t-h1">YOUR CART.</h1>
        {empty ? (
          <Card testId="cart-empty">
            <p className="t-strong">Your cart is empty.</p>
            <p className="t-body">Add your BREEZ routine from the Shop or any style&apos;s BREEZ match.</p>
            <Button testId="cart-go-shop" title="Shop BREEZ" onClick={() => navigate("/shop", { replace: true })} className="mt-3" />
          </Card>
        ) : (
          cart.lines.map((ln) => (
            <div key={ln.id} data-testid={`cart-line-${ln.handle}`} className="flex items-start gap-3 rounded-[20px] border border-line bg-white p-3">
              {ln.image ? <img src={ln.image} alt="" className="h-[72px] w-[72px] rounded-xl bg-tert object-cover" /> : <div className="h-[72px] w-[72px] rounded-xl bg-tert" />}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="t-strong line-clamp-2">{ln.title}</p>
                {ln.variant_title !== "Default Title" ? <p className="t-caption">{ln.variant_title}</p> : null}
                <p className="t-caption">{formatMoney(ln.unit_price)} each</p>
                {!ln.available ? <p className="t-caption !text-[#EF4444]">Now sold out</p> : null}
                <div className="mt-1 flex items-center gap-2">
                  <IconCircle disabled={cartBusy} testId={`cart-minus-${ln.handle}`} label={ln.quantity <= 1 ? "Remove" : "Decrease quantity"} icon={ln.quantity <= 1 ? Trash2 : Minus} onClick={() => (ln.quantity <= 1 ? removeLine(ln.id) : updateLine(ln.id, ln.quantity - 1))} />
                  <span data-testid={`cart-qty-${ln.handle}`} className="min-w-[20px] text-center font-display-bold text-base">{ln.quantity}</span>
                  <IconCircle disabled={cartBusy} testId={`cart-plus-${ln.handle}`} label="Increase quantity" icon={Plus} onClick={() => updateLine(ln.id, ln.quantity + 1)} />
                  <span className="flex-1" />
                  <span data-testid={`cart-line-total-${ln.handle}`} className="font-display-bold text-[15px]">{formatMoney(ln.line_total)}</span>
                </div>
              </div>
              <IconCircle disabled={cartBusy} testId={`cart-remove-${ln.handle}`} label="Remove item" icon={X} onClick={() => removeLine(ln.id)} />
            </div>
          ))
        )}
      </Page>
      {!empty ? (
        <Footer>
          <div className="flex w-full flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="t-label">Subtotal ({cart.total_quantity} item{cart.total_quantity === 1 ? "" : "s"})</span>
              <span data-testid="cart-subtotal" className="font-display text-[22px]">{formatMoney(cart.subtotal)}</span>
            </div>
            <p className="t-caption">Shipping &amp; taxes calculated at checkout on breez.au. Checkout opens in a new tab — your cart refreshes when you come back.</p>
            <Button testId="cart-checkout" title="Checkout on breez.au" icon={Lock} disabled={cartBusy} onClick={checkout} />
            <p className="flex items-center justify-center gap-1.5 t-caption"><ShieldCheck className="h-3.5 w-3.5" aria-hidden /> Official Shopify checkout. BREEZ AI never handles payment.</p>
          </div>
        </Footer>
      ) : null}
    </div>
  );
}
